import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminPipelineAPI } from './pipeline';

//admin API'S

// Shared cross-slice invalidation: a freshly-added lead / created
// application appears on both the Loan Information leads page (this
// slice's 'updateLoanInfo' tag, handled via invalidatesTags) AND on the
// Pipeline page (adminPipelineAPI's 'updateAdminPipeline'). RTK Query
// tag invalidation doesn't cross slices, so the pipeline poke has to
// happen explicitly here.
const _invalidateAdminPipelineOnSuccess = async (
  _arg,
  { dispatch, queryFulfilled }
) => {
  try {
    await queryFulfilled;
    dispatch(adminPipelineAPI.util.invalidateTags(['updateAdminPipeline']));
  } catch {
    // Creation failed; pipeline cache stays valid.
  }
};

export const loanInformationAPI = createApi({
  reducerPath: 'loanInformation',
  tagTypes: ['updateLoanInfo', 'dashboardData', 'BorrowerInvite'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    // add leads
    addLeads: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/lead`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateAdminPipelineOnSuccess,
      invalidatesTags: ['updateLoanInfo'],
    }),
    createConsumerApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/application/new`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateAdminPipelineOnSuccess,
      invalidatesTags: ['updateLoanInfo'],
    }),
    // lead gen: loan officers in the lead's branch it may hand the lead to
    getAssignableOfficers: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/application/assignable_officers/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    // lead gen: hand the lead to a loan officer (one time)
    assignLoanOfficer: builder.mutation({
      query: ({ applicationId, loanOfficerId }) => ({
        url: `b2b/loan/application/assign_officer/${applicationId}`,
        method: 'PUT',
        body: { loan_officer_id: loanOfficerId },
        headers: getHeaders(),
      }),
      invalidatesTags: ['updateLoanInfo', 'dashboardData'],
      // The lead may be on the Pipeline page, too.
      onQueryStarted: _invalidateAdminPipelineOnSuccess,
    }),
    // where the borrower's invite stands: not_sent / sent / send_failed /
    // expired / joined (no_account when the borrower has none)
    getBorrowerInviteStatus: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/application/borrower_invite_status/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (_result, _error, { applicationId }) => [{ type: 'BorrowerInvite', id: applicationId }],
    }),
    // email the borrower their invite (until they join)
    sendBorrowerInvite: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loan/application/send_borrower_invite/${applicationId}`,
        method: 'POST',
        headers: getHeaders(),
      }),
      // A failed send is recorded too, so the status refreshes either way.
      invalidatesTags: (_result, _error, { applicationId }) => [
        'updateLoanInfo',
        { type: 'BorrowerInvite', id: applicationId },
      ],
    }),
    // the borrower's invite link, for the user to send themselves; nothing is emailed
    createBorrowerInviteLink: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loan/application/borrower_invite_link/${applicationId}`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: (_result, error, { applicationId }) =>
        error ? [] : [{ type: 'BorrowerInvite', id: applicationId }],
    }),
    //get loan officer list
    getLoanOfficers: builder.query({
      query: () => {
        return {
          url: `/b2b/user/loan_officers/`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    //get leads table
    getAllLeads: builder.query({
      query: ({ data, paging, searchFilter }) => {
        return {
          url: `/b2b/loan/loa/lead_info?page_size=${paging?.limit}&page=${paging?.offset}&search=${searchFilter}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      providesTags: ['updateLoanInfo'],
    }),
    //get leads detail
    getLeadDetails: builder.query({
      query: ({ user_id, application_id }) => {
        return {
          url: `/b2b/loan/dashboard/view_lead_details?user_id=${user_id}&application_id=${application_id}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getDashboardInformation: builder.query({
      query: () => {
        return {
          url: `b2b/loan/loa/dashboard_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getDashboardSummary: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/summary`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['dashboardData'],
    }),
    getDashboardNewLeads: builder.query({
      query: ({ page = 1, page_size = 10 } = {}) => ({
        url: `b2b/loan/dashboard/new_leads?page=${page}&page_size=${page_size}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['dashboardData'],
    }),
    getDashboardPipeline: builder.query({
      query: ({ page = 1, page_size = 10 } = {}) => ({
        url: `b2b/loan/dashboard/pipeline?page=${page}&page_size=${page_size}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['dashboardData'],
    }),
    getDashboardLenderSpread: builder.query({
      query: () => ({
        url: `b2b/loan/dashboard/lender-spread`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
    }),
  }),
});

export const {
  useAddLeadsMutation,
  useCreateConsumerApplicationMutation,
  useGetAssignableOfficersQuery,
  useAssignLoanOfficerMutation,
  useGetBorrowerInviteStatusQuery,
  useSendBorrowerInviteMutation,
  useCreateBorrowerInviteLinkMutation,
  useGetLoanOfficersQuery,
  useGetAllLeadsQuery,
  useLazyGetAllLeadsQuery,
  useLazyGetLeadDetailsQuery,
  useGetDashboardInformationQuery,
  useGetDashboardSummaryQuery,
  useGetDashboardNewLeadsQuery,
  useGetDashboardPipelineQuery,
  useGetDashboardLenderSpreadQuery,
} = loanInformationAPI;
